import React, {CSSProperties, MouseEventHandler, ReactElement, useState} from 'react';
import classNames from 'classnames';
import {Icon, FolderIcon, ThemeName, Theme} from '@taopix/taopix-design-system';
import {Common} from '@taopix/taopix-core';

export type MediaGalleryAlbumThumbnailProps = {
  albumId: string | number;
  imgSource: string | ReactElement;
  albumName: string;
  onSelect: MouseEventHandler;
  pictureCount?: number;
  columns?: number;
  usePictureCount?: boolean;
};

const MediaGalleryAlbumThumbnail = ({
  albumId: folderId,
  imgSource,
  albumName,
  onSelect,
  pictureCount,
  usePictureCount = true,
  columns,
  ...props
}: MediaGalleryAlbumThumbnailProps) => {
  const [loaded, setLoaded] = useState(typeof imgSource === 'string' ? false : true);

  const loadFunc = () => {
    setLoaded(true);
  };

  let pictureCountText;

  if (undefined !== pictureCount) {
    pictureCountText = pictureCount + ' pictures';
  }

  let cssProperties = {};

  if (columns) {
    cssProperties = {'--columns': columns} as CSSProperties;
  }

  const active = usePictureCount ? pictureCount && pictureCount > 0 : true;

  const containerClasses = classNames([
    'group',
    'ml-[var(--grid-gap)]',
    'mt-[var(--grid-gap)]',
    'flex',
    'mb-xl',
    !active && 'opacity-30',
    columns
      ? 'w-[calc((100%-(var(--grid-gap)*(var(--columns))))/var(--columns))]'
      : 'w-[calc(var(--target-thumbnail-height)*1rem)]',
  ]);

  const buttonClasses = classNames(
    'flex',
    'flex-1',
    'flex-col',
    'items-start',
    'w-full',
    'h-full',
    'text-left',
    !active && 'cursor-default'
  );

  const boxClasses = classNames(
    'relative',
    'flex',
    'h-full',
    'before:block',
    'before:pt-[100%]',
    'items-center',
    'justify-center',
    'overflow-hidden',
    'w-full',
    active && [
      'group-hover:outline',
      'group-hover:outline-[2rem]',
      'group-hover:outline-solid',
      'group-hover:outline-offset-[-2rem]',
      'group-hover:outline-themeAccentColour',
    ]
  );

  const imageWrapperClasses = classNames('absolute');

  const imageClasses = ['h-full', 'object-cover', 'w-full'];

  return (
    <li className={containerClasses} style={cssProperties}>
      <button onClick={active ? onSelect : undefined} className={buttonClasses}>
        <Theme name={ThemeName.Container} className={boxClasses}>
          {typeof imgSource === 'string' ? (
            <div className={imageWrapperClasses}>
              {!loaded && <Icon icon={<FolderIcon />} size={'large'} />}
              <img
                style={loaded ? {} : {display: 'none'}}
                alt={albumName}
                src={imgSource}
                className={classNames(imageClasses)}
                onLoad={loadFunc}
              />
            </div>
          ) : (
            <div className="">{imgSource}</div>
          )}
        </Theme>
        <div className={'mt-sm'}>
          <span className={'block'}>{Common.getCurrentLocaleString(albumName, true)}</span>
          <span className={`block ${active && 'font-bold'}`}>{pictureCountText}</span>
        </div>
      </button>
    </li>
  );
};

export default MediaGalleryAlbumThumbnail;
